import { IAppState, ILog, IManualShipStatusData, IStatus, IStatusType } from "../../Types"
import { Icons } from "../../Components/Icon"
import { UserAdviceLevel, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { referenceDate } from "../../App/Format"
import { logger } from "../../App/Logger"
import { FocusViewState } from "../FocusView"
import { UserAdvice, UserAdviceInstance, UserAdviceLocation, UserAdviceState } from "./Advice"
import { OverlayViewDefinition } from "../OverlayView"
import { userAdviceManualShipStatusOverlay } from "./ManualShipStatusView"

export class ManualShipStatusAdvice extends UserAdvice {
  readonly role = UserAdviceRole.action;
  active: boolean = false;
  status?: IStatus = undefined;
  // The mode's expiry, on the app's `timeUtc` epoch (seconds since 2001-01-01 UTC),
  // and the current log's time / timezone offset the button and dialog read it
  // against. All three are only set while `active`.
  expiresAtUtc?: number = undefined;
  timeUtc: number = 0;
  timezone: number = 0;
  static level: number = 95;

  // The manual ship-status mode (#511): while it runs, navigation makes no automatic
  // ship-status changes at all and the crew sets the status themselves. An OPEN
  // `.manualShipStatus` status IS the on-signal (closing it via `endLogId` the
  // off-signal), so -- unlike the anchor and depth watches, which also offer an
  // "enable" entry point while inactive -- this advice exists ONLY while the mode is
  // actually running. Switching it on lives in the LogButton menu instead, because
  // the mode is a deliberate, occasional action rather than a configurable watch.
  update(_app: IAppState, state: FocusViewState, currentLog?: ILog) {
    this.active = false;
    this.status = undefined;
    this.expiresAtUtc = undefined;
    this.timeUtc = 0;
    this.timezone = 0;
    if (!currentLog) {
      return;
    }
    const status = UserAdvice.status(state.statuses, IStatusType.manualShipStatus, currentLog.dayId)
      .filter((s) => s.endLogId == undefined)
      .toSorted((a, b) => b.id - a.id)[0];
    if (!status) {
      return;
    }
    const expiresAtUtc = ManualShipStatusAdvice.expiresAtUtc(status);
    // Without a readable expiry there is nothing to show and nothing sensible to
    // extend from, so stay silent rather than render a mode with no clock.
    if (expiresAtUtc == undefined) {
      return;
    }
    this.status = status;
    this.expiresAtUtc = expiresAtUtc;
    this.timeUtc = currentLog.timeUtc;
    this.timezone = currentLog.timezone;
    this.active = true;
  }

  // The payload's `expiresAt` is an absolute ISO date (Swift `Date`), deliberately
  // absolute rather than a remaining duration so a restart mid-mode resumes with the
  // correct clock. Converted here to the `timeUtc` epoch the logs are measured in.
  private static expiresAtUtc(status: IStatus): number | undefined {
    try {
      const expiresAt = (JSON.parse(status.data || "{}") as IManualShipStatusData)?.expiresAt;
      if (!expiresAt) {
        return undefined;
      }
      const ms = new Date(expiresAt).valueOf();
      return isNaN(ms) ? undefined : (ms - referenceDate) / 1000;
    } catch (e) {
      logger.debug("ManualShipStatusAdvice expiresAtUtc", e);
      return undefined;
    }
  }

  // Measured against the CURRENT LOG's time rather than the wall clock -- the same
  // source StopUserAdvice counts its "Stopped for ..." from, so the value advances
  // with the live log broadcast (which is what re-runs `update`) and needs no timer
  // of its own. Rounded up so a mode with seconds left never reads as "0 minutes",
  // and clamped at 0: expiry belongs to navigation, which closes the status on its
  // own tick, so a lapsed mode keeps rendering here until that arrives.
  remainingMinutes(): number {
    if (this.expiresAtUtc == undefined) {
      return 0;
    }
    return Math.max(0, Math.ceil((this.expiresAtUtc - this.timeUtc) / 60));
  }

  // The mode's end moment in board time, the way TrackView renders a board clock:
  // the UTC value shifted by the log's own timezone offset.
  endTime(app: IAppState): string {
    if (this.expiresAtUtc == undefined) {
      return "";
    }
    return app.format("timeUtc", this.expiresAtUtc + this.timezone);
  }

  getInstances(app: IAppState, location: UserAdviceLocation, currentLog?: ILog): UserAdviceInstance[] {
    // Only on the current log row: the mode gates what navigation records for the
    // live log, so there is nothing to say about it on the day summary.
    if (!this.active || !currentLog || location != UserAdviceLocation.log) {
      return [];
    }
    const t = app.getT("UserAdviceManualShipStatusView");
    return [
      new UserAdviceInstance("manual-ship-status", ManualShipStatusAdvice.level, { icon: Icons.AdviceManualShipStatus, level: UserAdviceLevel.notice, shortTitle: t("title_short"), title: t("title_active", { time: this.endTime(app) }), type: "manual-ship-status" }, this, 0, location),
    ];
  }

  overlay(_app: IAppState, advice: UserAdviceInstance, state: UserAdviceState): OverlayViewDefinition | undefined {
    return userAdviceManualShipStatusOverlay({ advice, state });
  }
}
